// Phase 2.16 — REST-backed attendanceService.
//
// Mirrors the legacy attendanceService surface in firebaseService.js
// (lines 978–1297) so the selector at the bottom of firebaseService.js
// can swap implementations transparently. Re-exports
// `ATTENDANCE_STATUSES` and `summarizeAttendance` so consumers that
// import from this module get the same values as the Firebase variant.

import { getAccessToken } from '../contexts/AuthContextV2';
import {
  ATTENDANCE_STATUSES as ATTENDANCE_STATUSES_FIREBASE,
  summarizeAttendance as summarizeAttendanceFirebase
} from './attendanceConstants';

const API_BASE = process.env.REACT_APP_API_URL ?? '';

async function apiFetch(path, options = {}) {
  const token = getAccessToken();
  const headers = { 'Content-Type': 'application/json', ...(options.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}${path}`, { ...options, headers, credentials: 'include' });
  let body = null;
  try { body = await res.json(); } catch { /* non-json */ }
  if (!res.ok) {
    const err = new Error(body?.error || `Request failed: ${res.status}`);
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

const isValidStatus = (s) =>
  Object.values(ATTENDANCE_STATUSES_FIREBASE).includes(s);

const toDateStr = (d) =>
  typeof d === 'string' ? d : d.toISOString().split('T')[0];

export const attendanceService = {
  async setAttendance({
    studentId,
    classId,
    date,
    status,
    notes = '',
    lateMinutes = null,
    reasonText = '',
    reasonAudioUrl = null
    // tenantId is pinned server-side from the JWT — ignored here.
  }) {
    if (!studentId) throw new Error('setAttendance: studentId is required');
    if (!isValidStatus(status)) {
      throw new Error(`setAttendance: invalid status "${status}"`);
    }
    const dateStr = toDateStr(date);
    const body = {
      studentId,
      classId: classId || null,
      date: dateStr,
      status,
      notes,
      lateMinutes:
        status === ATTENDANCE_STATUSES_FIREBASE.LATE && Number.isFinite(Number(lateMinutes))
          ? Number(lateMinutes)
          : null,
      reasonText: reasonText || '',
      reasonAudioUrl: reasonAudioUrl || null
    };
    await apiFetch('/api/attendance', { method: 'POST', body: JSON.stringify(body) });
    return `${dateStr}_${studentId}`;
  },

  async bulkSetAttendance({
    classId,
    date,
    entries = [],
    previous = null
    // tenantId / actor pinned server-side from the JWT.
  }) {
    if (!Array.isArray(entries) || entries.length === 0) return 0;
    const dateStr = toDateStr(date);
    const cleaned = entries
      .filter((e) => e?.studentId && isValidStatus(e.status))
      .map((e) => ({
        studentId: e.studentId,
        status: e.status,
        notes: e.notes || '',
        lateMinutes:
          e.status === ATTENDANCE_STATUSES_FIREBASE.LATE && Number.isFinite(Number(e.lateMinutes))
            ? Number(e.lateMinutes)
            : null,
        reasonText: e.reasonText || '',
        reasonAudioUrl: e.reasonAudioUrl || null
      }));
    if (cleaned.length === 0) return 0;
    const body = {
      classId: classId || null,
      date: dateStr,
      entries: cleaned,
      ...(previous ? { previous } : {})
    };
    const { count } = await apiFetch('/api/attendance/bulk', {
      method: 'POST',
      body: JSON.stringify(body)
    });
    return count ?? cleaned.length;
  },

  async getAttendanceAudit({ classId, date }) {
    const dateStr = toDateStr(date);
    try {
      const params = new URLSearchParams({ date: dateStr });
      if (classId) params.set('classId', classId);
      const { audit } = await apiFetch(`/api/attendance/audit?${params.toString()}`);
      return Array.isArray(audit) ? audit : [];
    } catch (error) {
      console.warn('getAttendanceAudit failed:', error.message);
      return [];
    }
  },

  async getAttendanceForDay({ classId, date }) {
    const dateStr = toDateStr(date);
    try {
      const params = new URLSearchParams({ date: dateStr });
      if (classId) params.set('classId', classId);
      const { attendance } = await apiFetch(`/api/attendance/day?${params.toString()}`);
      return attendance || {};
    } catch (error) {
      console.warn('getAttendanceForDay failed:', error.message);
      return {};
    }
  },

  async getAttendanceByStudentIds({ date, studentIds }) {
    const dateStr = toDateStr(date);
    if (!Array.isArray(studentIds) || studentIds.length === 0) return {};
    const { attendance } = await apiFetch('/api/attendance/by-students', {
      method: 'POST',
      body: JSON.stringify({ date: dateStr, studentIds })
    });
    return attendance || {};
  },

  async getAttendanceForTenant({ fromDate, toDate } = {}) {
    const from = typeof fromDate === 'string'
      ? fromDate
      : fromDate?.toISOString?.().split('T')[0];
    const to = typeof toDate === 'string'
      ? toDate
      : toDate?.toISOString?.().split('T')[0];
    if (!from || !to) return [];
    try {
      const params = new URLSearchParams({ from, to });
      const { attendance } = await apiFetch(`/api/attendance/tenant?${params.toString()}`);
      return Array.isArray(attendance) ? attendance : [];
    } catch (error) {
      console.warn('getAttendanceForTenant failed:', error.message);
      return [];
    }
  },

  // Server-side daily roll-up for the moudir attendance chart — returns
  // pre-aggregated `{ date, status, count }` rows (≤ days×4) instead of raw
  // attendance, so any range (incl. the 5-year 'all' window) is returned whole
  // without hitting the /tenant row cap.
  async getTenantAttendanceDaily({ fromDate, toDate } = {}) {
    const from = typeof fromDate === 'string'
      ? fromDate
      : fromDate?.toISOString?.().split('T')[0];
    const to = typeof toDate === 'string'
      ? toDate
      : toDate?.toISOString?.().split('T')[0];
    if (!from || !to) return [];
    try {
      const params = new URLSearchParams({ from, to });
      const { daily } = await apiFetch(`/api/attendance/tenant/daily?${params.toString()}`);
      return Array.isArray(daily) ? daily : [];
    } catch (error) {
      console.warn('getTenantAttendanceDaily failed:', error.message);
      return [];
    }
  },

  async getAttendanceForStudent(studentId, fromDate, toDate) {
    if (!studentId) return [];
    const fromStr = toDateStr(fromDate);
    const toStr = toDateStr(toDate);
    try {
      const params = new URLSearchParams({ from: fromStr, to: toStr });
      const { attendance } = await apiFetch(
        `/api/attendance/student/${encodeURIComponent(studentId)}?${params.toString()}`
      );
      return Array.isArray(attendance) ? attendance : [];
    } catch (error) {
      console.warn('getAttendanceForStudent failed:', error.message);
      return [];
    }
  }
};

// Re-exports kept single-source: the constants and pure helper live in
// firebaseService.js so we don't fork them.
export const ATTENDANCE_STATUSES = ATTENDANCE_STATUSES_FIREBASE;
export const summarizeAttendance = summarizeAttendanceFirebase;

export default attendanceService;
